import React from "react";
import { formatDateTime } from "../utils/date";

type Complaint = {
  title: string;
  status: string;
  description: string;
  createdBy: string;
  createdAt: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  data: Complaint | null;
};

const getStatusStyle = (status: string) => {
  switch (status.toLowerCase()) {
    case "open":
      return "bg-blue-100 text-blue-700 border border-blue-200";
    case "pending":
      return "bg-yellow-100 text-yellow-700 border border-yellow-200";
    case "resolved":
      return "bg-green-100 text-green-700 border border-green-200";
    default:
      return "bg-gray-100 text-gray-700 border border-gray-200";
  }
};

const ComplaintDetailsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  data,
}) => {
  if (!isOpen || !data) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      
      {/* Modal Card */}
      <div className="bg-white w-full max-w-xl rounded-3xl shadow-2xl p-6 animate-fadeIn">
        
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-gray-800">
              Complaint Details
            </h2>
            <p className="text-xs text-gray-400">
              View full complaint information
            </p>
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-100 text-gray-500 hover:text-red-500 transition"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="space-y-5 text-sm">
          
          {/* Title */}
          <div>
            <p className="text-gray-400 text-xs uppercase mb-1">Title</p>
            <p className="text-lg font-semibold text-gray-800">
              {data.title}
            </p>
          </div>

          {/* Status + Created By */}
          <div className="flex justify-between items-center gap-4">
            <div>
              <p className="text-gray-400 text-xs uppercase mb-1">Status</p>
              <span
                className={`px-3 py-1 text-xs rounded-full font-semibold ${getStatusStyle(
                  data.status
                )}`}
              >
                {data.status}
              </span>
            </div>

            <div className="text-right">
              <p className="text-gray-400 text-xs uppercase mb-1">
                Created By
              </p>
              <p className="font-medium text-gray-700">
                {data.createdBy} 
              </p>
            </div>
          </div>

          {/* Description */}
          <div>
            <p className="text-gray-400 text-xs uppercase mb-1">
              Description
            </p>
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-gray-700 leading-relaxed">
              {data.description}
            </div>
          </div>

          {/* Date */}
          <div>
            <p className="text-gray-400 text-xs uppercase mb-1">
              Created At
            </p>
            <p className="font-medium text-gray-700">
              {formatDateTime(data.createdAt)}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition shadow-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComplaintDetailsModal;